import React, { useState, useRef, useEffect } from "react";
import type {
  AppSettings,
  ClimatePledgeFriendlyFeatures,
  Certification,
} from "../types";
import { getFontName } from "../utils/definition-helpers";
import { SUSTAINABILITY_CSS_CLASSES } from "../../../../constants";
import { SheetHeader } from "./SheetHeader";
import { SheetFooter } from "./SheetFooter";
import { FeaturesDisplay } from "./FeaturesDisplay";
import { CertificationDetailView } from "./CertificationDetailView";
import { StandaloneCertificationDisplay } from "./StandaloneCertificationDisplay";
import { useComponentState } from "../hooks";
import { useMetricEmitter } from "../contexts/MetricEmitterContext";

interface SheetContentProps {
  settings: AppSettings;
  cpfData: ClimatePledgeFriendlyFeatures;
  onClose: () => void;
  focusManagement?: ReturnType<typeof import("../hooks").useFocusManagement>;
  shadowRoot?: ShadowRoot | null;
  isOpen: boolean;
}

export const SheetContent = ({
  settings,
  cpfData,
  onClose,
  focusManagement,
  shadowRoot,
  isOpen,
}: SheetContentProps) => {
  const { certification } = useComponentState();
  const isInitialMountRef = useRef(true);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const metricEmitter = useMetricEmitter();

  const isDetailView = !!certification.selected;

  useEffect(() => {
    if (!isOpen) {
      certification.clear();
    }
  }, [isOpen, certification]);

  // Track pd (Progressive Disclosure) render latency when sheet content is ready
  useEffect(() => {
    requestAnimationFrame(() => {
      metricEmitter.trackLatency("sheet");
    });
  }, [metricEmitter]);

  // Apply font family CSS custom property to the sheet content container
  const fontName = getFontName(settings["font-family"] || "amazon-ember");
  const containerStyle: React.CSSProperties = {
    [SUSTAINABILITY_CSS_CLASSES.fontFamilyProperty]: `"${fontName}"`,
  } as React.CSSProperties;

  // Apply theme class for color inheritance
  const colorMode = settings["color-mode"];
  const themeClass = `sustainability-theme-${colorMode}`;

  const handleCertificationSelect = (
    cert: Certification,
    clickedElement?: HTMLElement,
  ) => {
    // Store the clicked certification element for focus restoration
    if (clickedElement) {
      previouslyFocusedElementRef.current = clickedElement;
    }
    certification.select(cert);
  };

  const handleBackToFeatures = () => {
    certification.goBack();
  };

  // Check if we're showing standalone certification
  const hasStandaloneCertification = !!cpfData.standaloneCertifications?.[0];

  // Restore focus when returning to main view
  useEffect(() => {
    if (!isDetailView && previouslyFocusedElementRef.current) {
      requestAnimationFrame(() => {
        previouslyFocusedElementRef.current?.focus({ preventScroll: true });
        previouslyFocusedElementRef.current = null;
      });
    }
  }, [isDetailView]);

  // Focus close button when sheet first opens (primary view only on initial mount)
  useEffect(() => {
    if (isInitialMountRef.current && !isDetailView && focusManagement) {
      focusManagement.focusCloseButton("sheet-close-button", shadowRoot);
      isInitialMountRef.current = false;
    }
  }, [isDetailView, focusManagement, shadowRoot]);

  return (
    <div className={`sheet-content ${themeClass}`} style={containerStyle}>
      {/* Dynamic Sheet Header based on current view */}
      {isDetailView && certification.selected ? (
        <SheetHeader
          title={certification.selected.displayName}
          onClose={onClose}
          onBack={handleBackToFeatures}
          mode="certification-detail"
          autoFocusBack={true}
        />
      ) : cpfData.features?.length > 0 ? (
        <SheetHeader
          title={cpfData?.disclosureHeading || "Sustainability features"}
          description={cpfData?.description}
          icon={cpfData?.icon}
          onClose={onClose}
        />
      ) : hasStandaloneCertification ? (
        <SheetHeader
          title="Sustainability certifications"
          description="Products with trusted sustainability certification(s)."
          icon={cpfData?.icon}
          onClose={onClose}
        />
      ) : null}

      <div className="sheet-content__main">
        {!isDetailView ? (
          // Main features view
          <>
            {cpfData.features?.length > 0 ? (
              <FeaturesDisplay
                features={cpfData.features}
                onCertificationSelect={handleCertificationSelect}
              />
            ) : (
              cpfData.standaloneCertifications &&
              cpfData.standaloneCertifications.length > 0 && (
                <StandaloneCertificationDisplay
                  certifications={cpfData.standaloneCertifications}
                  onCertificationSelect={handleCertificationSelect}
                />
              )
            )}
          </>
        ) : (
          // Certification detail view
          certification.selected && (
            <CertificationDetailView
              certification={certification.selected}
              metricEmitter={metricEmitter}
            />
          )
        )}
      </div>

      {/* Shared Footer - only show in main features view, not in certification detail */}
      {!isDetailView && <SheetFooter program={cpfData.program} />}
    </div>
  );
};
